import { useCallback, useEffect, useMemo, useState } from 'react'

type ZoomLabel = string | number

export interface ZoomState {
  startIndex: number | null
  endIndex: number | null
}

export interface UseChartZoomOptions<T> {
  data: T[]
  enabled?: boolean
  minSelectionSize?: number
}

export interface UseChartZoomResult<T> {
  zoomedData: T[]
  isZoomed: boolean
  selectionStart: ZoomLabel | null
  selectionEnd: ZoomLabel | null
  isSelecting: boolean
  handleMouseDown: (e: { activeLabel?: ZoomLabel } | null) => void
  handleMouseMove: (e: { activeLabel?: ZoomLabel } | null) => void
  handleMouseUp: () => void
  resetZoom: () => void
}

export const useChartZoom = <T extends { timestamp: ZoomLabel | null }>({
  data,
  enabled = true,
  minSelectionSize = 2,
}: UseChartZoomOptions<T>): UseChartZoomResult<T> => {
  const [zoomRange, setZoomRange] = useState<{ start: number; end: number } | null>(null)
  const [selectionStart, setSelectionStart] = useState<ZoomLabel | null>(null)
  const [selectionEnd, setSelectionEnd] = useState<ZoomLabel | null>(null)
  const [isSelecting, setIsSelecting] = useState(false)

  const getDataIndex = useCallback(
    (label: ZoomLabel | undefined): number => {
      if (!label) return -1
      return data.findIndex(d => d.timestamp === label)
    },
    [data]
  )

  const handleMouseDown = useCallback(
    (e: { activeLabel?: ZoomLabel } | null) => {
      if (!enabled || !e?.activeLabel) return
      setIsSelecting(true)
      setSelectionStart(e.activeLabel)
      setSelectionEnd(e.activeLabel)
    },
    [enabled]
  )

  const handleMouseMove = useCallback(
    (e: { activeLabel?: ZoomLabel } | null) => {
      if (!isSelecting || !e?.activeLabel) return
      setSelectionEnd(e.activeLabel)
    },
    [isSelecting]
  )

  const handleMouseUp = useCallback(() => {
    if (!isSelecting || !selectionStart || !selectionEnd) {
      setIsSelecting(false)
      setSelectionStart(null)
      setSelectionEnd(null)
      return
    }

    const startIdx = getDataIndex(selectionStart)
    const endIdx = getDataIndex(selectionEnd)

    if (startIdx === -1 || endIdx === -1) {
      setIsSelecting(false)
      setSelectionStart(null)
      setSelectionEnd(null)
      return
    }

    const minIdx = Math.min(startIdx, endIdx)
    const maxIdx = Math.max(startIdx, endIdx)

    if (maxIdx - minIdx >= minSelectionSize - 1) {
      setZoomRange({ start: minIdx, end: maxIdx })
    }

    setIsSelecting(false)
    setSelectionStart(null)
    setSelectionEnd(null)
  }, [isSelecting, selectionStart, selectionEnd, getDataIndex, minSelectionSize])

  const resetZoom = useCallback(() => {
    setZoomRange(null)
  }, [])

  useEffect(() => {
    setZoomRange(null)
    setSelectionStart(null)
    setSelectionEnd(null)
    setIsSelecting(false)
  }, [data.length, data[0]?.timestamp, data[data.length - 1]?.timestamp])

  const zoomedData = useMemo(() => {
    if (!zoomRange) return data
    return data.slice(zoomRange.start, zoomRange.end + 1)
  }, [data, zoomRange])

  return {
    zoomedData,
    isZoomed: zoomRange !== null,
    selectionStart,
    selectionEnd,
    isSelecting,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    resetZoom,
  }
}
